import { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  limit,
  startAfter,
  getCountFromServer,
  orderBy,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase-config"; // Adjust the import according to your project structure
import { useNotification } from "../components/Notification";
import { useAppState } from "../appContext";
import { GeoFirestore } from "geofirestore"; // Ensure correct import path
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import moment from "moment";
import { fetchJSON } from "./scraperHooks";
const currentDate = new Date();
const fourHoursBefore = new Date(
  currentDate.setHours(currentDate.getHours() - 4)
);

const RAW_ADMIN_API = (process.env.REACT_APP_ADMIN_API || "").trim();
const ADMIN_API = RAW_ADMIN_API.replace(/\/$/, "");
const ADMIN_TOKEN = (process.env.REACT_APP_ADMIN_TOKEN || "").trim();
const adminAuthHeader = ADMIN_TOKEN ? { "x-admin-token": ADMIN_TOKEN } : {};

const adminBase = () => ADMIN_API || "";

async function requestEmbedding(eventId, force = false) {
  if (!eventId) return { ok: false, warning: "missing_event_id" };
  const url = `${adminBase()}/api/admin/events/${eventId}/embedding`;
  return fetchJSON(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...adminAuthHeader,
    },
    body: JSON.stringify({ force }),
  });
}

// Custom hook to fetch all events from Firestore
const geoFirestore = new GeoFirestore(db);
const parseQueryString = (field, operator, value) => {
  switch (operator) {
    case "==":
      return where(field, "==", value);
    case "!=":
      return where(field, "!=", value);
    case "<":
      return where(field, "<", value);
    case "<=":
      return where(field, "<=", value);
    case ">":
      return where(field, ">", value);
    case ">=":
      return where(field, ">=", value);
    case "array-contains":
      return where(field, "array-contains", value);
    case "in":
      return where(field, "in", value);
    case "array-contains-any":
      return where(field, "array-contains-any", value);
    default:
      throw new Error(`Unsupported query operator: ${operator}`);
  }
};

const sortEvents = (events) => {
  const now = moment(); // Get the current time

  // Separate future/current events and past events
  const futureEvents = events.filter((event) =>
    moment(event.main_date).isSameOrAfter(now)
  );
  const pastEvents = events.filter((event) =>
    moment(event.main_date).isBefore(now)
  );

  // Sort future/current events in ascending order
  futureEvents.sort((a, b) => moment(a.main_date) - moment(b.main_date));

  // Sort past events in descending order
  pastEvents.sort((a, b) => moment(b.main_date) - moment(a.main_date));

  // Combine the sorted arrays
  return [...futureEvents, ...pastEvents];
};

const fetchStandardFilteredRecords = async (filters, take, lastDoc, db) => {
  let orderByField = "main_date";
  const inequalities = ["<", "<=", "!=", "not-in", ">", ">="];
  let eventQuery = null;
  eventQuery = collection(db, "events");

  const hasInEquality =
    filters.filter((f) => inequalities.includes(f.operator)).length > 0;
  const hasSearchFilter =
    filters.filter((f) => f.field === "title_lowercase").length > 0;
  if (hasSearchFilter) {
    orderByField = "title_lowercase";
  }

  for (const { field, operator, value } of filters) {
    eventQuery = query(eventQuery, parseQueryString(field, operator, value));
  }
  if (!hasInEquality) {
    eventQuery = query(eventQuery, where("main_date", ">=", fourHoursBefore));
  }

  // Only fetch the total count on the FIRST page (lastDoc === null).
  // On subsequent pages we already have the count from the first fetch,
  // so skipping getCountFromServer halves the Firestore reads for pagination.
  let totalCount = 0;
  if (!lastDoc) {
    const countSnapshot = await getCountFromServer(eventQuery);
    totalCount = countSnapshot.data().count;
  }

  eventQuery = query(eventQuery, orderBy(orderByField, "asc"), limit(take));
  if (lastDoc) {
    eventQuery = query(eventQuery, startAfter(lastDoc));
  }

  const querySnapshot = await getDocs(eventQuery);
  const docs = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
  return { docs, totalCount, lastVisible, isPaginated: true };
};

export const useFetchEvents = (
  take = 20,
  skip = 0,
  filters = [],
  enableApi = false,
  refreshTrigger = 0
) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [isPaginated, setIsPaginated] = useState(true);
  const [lastDoc, setLastDoc] = useState(null); // Track the last document for pagination
  const [prevFilterString, setPrevFilterString] = useState(""); // Track previous filters
  const [refresh, setRefresh] = useState(0);
  const localEventsRef = useRef([]); // Ref to store all fetched records locally
  const isFetchingRef = useRef(false); // Ref to track if a fetch operation is in progress

  useEffect(() => {
    setLoading(true);
    if (!enableApi) {
      return;
    }
    const currentFilterString = JSON.stringify(filters);

    // Reset lastDoc and localEvents if filters have changed
    if (
      currentFilterString !== prevFilterString ||
      refresh !== refreshTrigger
    ) {
      isFetchingRef.current = false;
      setLastDoc(null);
      localEventsRef.current = []; // Clear local events in the ref
      setPrevFilterString(currentFilterString); // Update the previous filter string
    }

    // Check if the local data covers the requested range
    if (localEventsRef.current.length >= skip + take) {
      setEvents(
        sortEvents(
          localEventsRef.current.slice(skip, skip + take).map((d) => {
            const mainDate = d.main_date.toDate(); // Convert Firestore Timestamp to JavaScript Date
            return {
              ...d,
              main_date: moment(mainDate).format("YYYY-MM-DD HH:mm:ss"),
              modiefieddate: moment(d.modifieddate?.toDate()).format(
                "YYYY-MM-DD HH:mm:ss"
              ),
            };
          })
        )
      );
      setLoading(false);
      return; // No need to proceed further if local data is sufficient
    }

    // Prevent overlapping fetches using ref
    if (isFetchingRef.current) return;

    // If local data is not sufficient, fetch from Firestore
    const fetchEvents = async () => {
      setLoading(true);

      setLoading(true);
      isFetchingRef.current = true; // Mark fetching as in progress
      try {
        let result;
        // Only Standard Filters applied
        result = await fetchStandardFilteredRecords(
          filters,
          take,
          currentFilterString !== prevFilterString ? null : lastDoc,
          db
        );

        // Append new results to localEventsRef
        localEventsRef.current = [...localEventsRef.current, ...result.docs];
        // Process and set events with the necessary formatting and sorting
        setEvents(
          sortEvents(
            (result.isPaginated
              ? localEventsRef.current.slice(skip, skip + take)
              : localEventsRef.current
            ) // No slicing if not paginated
              .map((d) => {
                const mainDate = d.main_date.toDate(); // Convert Firestore Timestamp to JavaScript Date
                return {
                  ...d,
                  main_date: moment(mainDate).format("YYYY-MM-DD HH:mm:ss"),
                  modifieddate: moment(d.modifieddate?.toDate()).format(
                    "YYYY-MM-DD HH:mm:ss"
                  ),
                };
              })
          )
        );

        // Only update the total count on the first page fetch (totalCount > 0).
        // On pagination pages getCountFromServer is skipped, so totalCount is 0
        // and we keep the value from the first fetch.
        if (result.totalCount > 0) {
          setTotalItems(result.totalCount);
        }
        setIsPaginated(result.isPaginated);
        setLastDoc(result.lastVisible || null);
        setRefresh(refreshTrigger);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
        isFetchingRef.current = false; // Fetching is complete
      }
    };

    fetchEvents(); // Call the fetch function if local data is insufficient
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [take, skip, filters, refreshTrigger]);

  return { events, loading, totalItems, isPaginated };
};

// End new fetch evemts

const PAGE_SIZE = 50;
// Module-level cache — survives route changes, restores on Back navigation
const venueEventsCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000;

const processEventDocs = (docs) =>
  sortEvents(
    docs.map((doc) => {
      const d = { id: doc.id, ...doc.data() };
      return {
        ...d,
        main_date: moment(d.main_date.toDate()).format("YYYY-MM-DD HH:mm:ss"),
        modifieddate: moment(d.modifieddate?.toDate()).format("YYYY-MM-DD HH:mm:ss"),
      };
    })
  );

const buildVenueEventsQuery = (eventsCollection, venue, showPastEvents, cursor = null) => {
  const now = new Date(Date.now() - 4 * 60 * 60 * 1000);
  let q;
  if (showPastEvents) {
    q = query(eventsCollection, where("venue", "==", venue), orderBy("main_date", "desc"), limit(PAGE_SIZE));
  } else {
    q = query(eventsCollection, where("venue", "==", venue), where("main_date", ">=", now), orderBy("main_date", "asc"), limit(PAGE_SIZE));
  }
  if (cursor) q = query(q, startAfter(cursor));
  return q;
};

export const useFetchEventsByVenueId = (venue, showPastEvents) => {
  const cacheKey = venue ? `${venue}-${showPastEvents}` : null;
  const cached = cacheKey ? venueEventsCache.get(cacheKey) : null;
  const isFresh = cached && Date.now() - cached.ts < CACHE_TTL_MS;

  const [events, setEvents] = useState(isFresh ? cached.events : []);
  const [loading, setLoading] = useState(!isFresh && !!venue);
  const [lastDoc, setLastDoc] = useState(isFresh ? cached.lastDoc : null);
  const [hasMore, setHasMore] = useState(isFresh ? cached.hasMore : false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    if (!venue) return;
    const key = `${venue}-${showPastEvents}`;
    const c = venueEventsCache.get(key);
    if (c && Date.now() - c.ts < CACHE_TTL_MS) {
      setEvents(c.events);
      setLastDoc(c.lastDoc);
      setHasMore(c.hasMore);
      setLoading(false);
      return;
    }
    setLoading(true);
    setEvents([]);
    const fetchEvents = async () => {
      try {
        const snap = await getDocs(buildVenueEventsQuery(collection(db, "events"), venue, showPastEvents));
        const processed = processEventDocs(snap.docs);
        const last = snap.docs[snap.docs.length - 1] ?? null;
        const more = snap.size === PAGE_SIZE;
        setEvents(processed);
        setLastDoc(last);
        setHasMore(more);
        venueEventsCache.set(key, { events: processed, lastDoc: last, hasMore: more, ts: Date.now() });
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [venue, showPastEvents]);

  const loadMore = async () => {
    if (!lastDoc || !hasMore || isLoadingMore || loading) return;
    setIsLoadingMore(true);
    try {
      const snap = await getDocs(buildVenueEventsQuery(collection(db, "events"), venue, showPastEvents, lastDoc));
      const newProcessed = processEventDocs(snap.docs);
      const newLast = snap.docs[snap.docs.length - 1] ?? null;
      const more = snap.size === PAGE_SIZE;
      const combined = [...events, ...newProcessed];
      setEvents(combined);
      setLastDoc(newLast);
      setHasMore(more);
      const key = `${venue}-${showPastEvents}`;
      venueEventsCache.set(key, { events: combined, lastDoc: newLast, hasMore: more, ts: Date.now() });
    } catch (error) {
      console.error("Error loading more events:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return { events, loading, totalItems: events.length, hasMore, loadMore, isLoadingMore };
};

export const useFetchEventById = (eventId) => {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        if (!eventId) {
          setLoading(false);
          return;
        }
        setLoading(true);

        // Use compat SDK method instead of modular SDK (db is a compat instance)
        let eventDoc = await db.collection("events").doc(eventId).get();

        // Fallback: GeoFirestore.update() can move documents to a new Firestore doc ID
        // while keeping the old ID in the `id` field. Search by the internal id field.
        if (!eventDoc.exists) {
          const fallbackSnap = await db
            .collection("events")
            .where("id", "==", eventId)
            .limit(1)
            .get();
          if (!fallbackSnap.empty) {
            eventDoc = fallbackSnap.docs[0];
          }
        }

        if (eventDoc.exists) {
          const eventData = eventDoc.data();
          const mainDate = eventData.main_date?.toDate();
          const modifiedDate = eventData.modifieddate?.toDate();
          setEvent({
            ...eventData,
            // id MUST come after spread so it overrides the internal `id` field in the data.
            // GeoFirestore can move a doc to a new Firestore ID while keeping the old value
            // in the `id` field — we always want the actual Firestore doc ID here so that
            // subsequent updateEvent() calls target the right document.
            id: eventDoc.id,
            main_date: mainDate
              ? moment(mainDate).format("YYYY-MM-DD HH:mm:ss")
              : null,
            modifieddate: modifiedDate
              ? moment(modifiedDate).format("YYYY-MM-DD HH:mm:ss")
              : null,
          });
        } else {
          setError("Event not found");
        }
      } catch (err) {
        setError(`Error fetching event: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  return { event, loading, error };
};

export const useFetchEventsByBandId = (band, showPastEvents) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    setLoading(true);

    const fetchEvents = async () => {
      setLoading(true);
      try {
        const eventsCollection = collection(db, "events");
        let q;
        if (showPastEvents) {
          q = query(
            eventsCollection,
            where("bandIds", "array-contains", band),
            orderBy("main_date", "asc")
          );
        } else {
          q = query(
            eventsCollection,
            where("bandIds", "array-contains", band),
            where("main_date", ">=", fourHoursBefore),
            orderBy("main_date", "asc")
          );
        }

        const querySnapshot = await getDocs(q);

        const eventsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setEvents(
          sortEvents(
            eventsList.map((d) => {
              const mainDate = d.main_date.toDate(); // Convert Firestore Timestamp to JavaScript Date
              // const formattedMainDate = formatDate(mainDate);
              return {
                ...d,
                main_date: moment(mainDate).format("YYYY-MM-DD HH:mm:ss"),
                modifieddate: moment(d.modifieddate?.toDate()).format(
                  "YYYY-MM-DD HH:mm:ss"
                ),
              };
            })
          )
        );
        setTotalItems(querySnapshot.size);
      } catch (error) {
        alert("Error in fetching");
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    if (band) fetchEvents();
  }, [band, showPastEvents]);

  return { events, loading, totalItems };
};

// Custom hook to add a new event to Firestore
export const useAddEvent = () => {
  const { showSuccessMessage, showWarningMessage } = useNotification();
  const [loading, setLoading] = useState(false);

  const addEvent = async (eventData) => {
    try {
      setLoading(true);

      // Use compat API directly — GeoFirestore.set() was generating new Firestore doc IDs
      // instead of using the provided eventData.id, causing events to move to unexpected locations.
      await db.collection("events").doc(`${eventData.id}`).set({
        ...eventData,
        coordinates: new firebase.firestore.GeoPoint(
          parseFloat(eventData.lat) || 0.0,
          parseFloat(eventData.lon) || 0.0
        ),
        modifieddate: firebase.firestore.FieldValue.serverTimestamp(),
      });

      try {
        const embedResult = await requestEmbedding(eventData.id);
        if (embedResult?.warning) {
          showWarningMessage(
            "Event saved, but embedding failed. You can recompute from the event details.",
            embedResult?.error
          );
        }
      } catch (embedError) {
        showWarningMessage(
          "Event saved, but embedding failed. You can recompute from the event details.",
          embedError.message
        );
      }

      showSuccessMessage("Event added successfully");
    } catch (error) {
      console.error("Error adding event:", error);
      showWarningMessage("Error adding event:", error.message);
    } finally {
      setLoading(false);
    }
  };

  return { addEvent, loading };
};
// Custom hook to update an existing event in Firestore

export const useUpdateEvent = () => {
  const [loading, setLoading] = useState(false);
  const { showSuccessMessage, showWarningMessage } = useNotification();

  const updateEvent = async (eventId, eventData) => {
    try {
      setLoading(true);

      // Use compat API directly — GeoFirestore.update() was moving documents to new Firestore
      // doc IDs (deleting the original), causing "event not found" / blank forms on next edit.
      await db.collection("events").doc(eventId).update({
        ...eventData,
        coordinates: new firebase.firestore.GeoPoint(
          parseFloat(eventData.lat) || 0.0,
          parseFloat(eventData.lon) || 0.0
        ),
        modifieddate: firebase.firestore.FieldValue.serverTimestamp(),
      });

      try {
        const embedResult = await requestEmbedding(eventId);
        if (embedResult?.warning) {
          showWarningMessage(
            "Event updated, but embedding failed. You can recompute from the event details.",
            embedResult?.error
          );
        }
      } catch (embedError) {
        showWarningMessage(
          "Event updated, but embedding failed. You can recompute from the event details.",
          embedError.message
        );
      }

      showSuccessMessage("Event updated successfully");
    } catch (error) {
      console.error("Error updating event:", error);
      showWarningMessage("Error updating event:", error.message);
    } finally {
      setLoading(false);
    }
  };

  return { updateEvent, loading };
};

// Custom hook to delete an event from Firestore
export const useDeleteEvent = () => {
  const [loading, setLoading] = useState(false);
  const [state, dispatch] = useAppState();

  const { showSuccessMessage, showWarningMessage } = useNotification();

  const deleteEvent = async (eventId) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, "events", `${eventId}`));
      dispatch({
        type: "SET_PROP",
        payload: {
          key: "events",
          value: state?.events?.filter((e) => e.id !== eventId),
        },
      });
      showSuccessMessage("Event Deleted successfully");
    } catch (error) {
      showWarningMessage("Error Deleting event:", error);
    } finally {
      setLoading(false);
    }
  };

  return { deleteEvent, loading };
};
export const useFetchEventsByDate = (isFuture, take) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const eventsCollection = collection(db, "events");
        const q = query(
          eventsCollection,
          where("main_date", isFuture ? ">=" : "<", fourHoursBefore),
          orderBy("main_date", isFuture ? "asc" : "desc"),
          limit(take)
        );
        const querySnapshot = await getDocs(q);

        const eventsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setEvents(
          sortEvents(
            eventsList.map((d) => {
              const mainDate = d?.main_date?.toDate(); // Convert Firestore Timestamp to JavaScript Date
              // const formattedMainDate = formatDate(mainDate);
              return {
                ...d,
                main_date: moment(mainDate).format("YYYY-MM-DD HH:mm:ss"),
              };
            })
          )
        );
        setTotalItems(querySnapshot.size);

        console.log("Events Fetched From DB");
      } catch (error) {
        alert("Error in fetching");
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [isFuture, take]);

  return { events, loading, totalItems };
};
